import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { passwordsMatchValidator } from '../../shared/validators/password-match.validator';
import { Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { Store } from '@ngrx/store';
import { selectAuthUser } from '../../store/selectors/auth.selectors';

@Component({
  selector: 'app-update-password',
  standalone: false,
  templateUrl: './update-password.component.html',
  styleUrl: './update-password.component.scss',
})
export class UpdatePasswordComponent implements OnDestroy {
  email = '';
  oldPassword = '';
  password = '';
  confirmPassword = '';
  loading = false;
  showOldPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  isSubmitted = false;

  resetForm = this.fb.group(
    {
      oldPassword: ['', [Validators.required]],
      password: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/),
        ],
      ],
      confirmPassword: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/),
        ],
      ],
    },
    {
      validators: passwordsMatchValidator(),
    }
  );

  private destroy$ = new Subject<void>();

  constructor(
    private auth: AuthService,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private translate: TranslateService,
    private store: Store
  ) {
    this.store
      .select(selectAuthUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user && user.email) {
          this.email = user.email;
        }
      });
  }

  updatePassword() {
    this.isSubmitted = true;

    if (this.password !== this.confirmPassword) {
      this.toastr.error(this.translate.instant('PASSWORD_RESET_NOT_MATCH'));
      return;
    }

    this.loading = true;
    this.auth
      .updatePassword(
        this.email,
        this.oldPasswordControl?.value as string,
        this.newPasswordControl?.value as string
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success(this.translate.instant('PASSWORD_UPDATE_SUCCESS'));
          this.loading = false;
        },
        error: () => {
          this.toastr.error(this.translate.instant('PASSWORD_UPDATE_FAIL'));
          this.loading = false;
        },
      });
  }

  toggleOldPasswordVisibility(): void {
    this.showOldPassword = !this.showOldPassword;
  }

  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get formControls() {
    return this.resetForm.controls;
  }

  get newPasswordValue(): string {
    return this.newPasswordControl?.value || '';
  }

  get hasMinLength(): boolean {
    return this.newPasswordValue.length >= 6;
  }

  get hasUpperCase(): boolean {
    return /[A-Z]/.test(this.newPasswordValue);
  }

  get hasNumber(): boolean {
    return /\d/.test(this.newPasswordValue);
  }

  get hasSpecialChar(): boolean {
    return /[!@#$%^&*]/.test(this.newPasswordValue);
  }

  get oldPasswordControl() {
    return this.resetForm.get('oldPassword');
  }
  get newPasswordControl() {
    return this.resetForm.get('password');
  }
  get confirmPasswordControl() {
    return this.resetForm.get('confirmPassword');
  }
}
